import { useState } from 'react';
import { Check } from 'lucide-react';
import { USP_GRADE_SCALE } from '../constants';

// Use case 2: what raw mark does the final exam need for each grade tier?
// Coursework is entered as weighted points already earned (e.g. 50 of a
// possible 60%), so tier% − coursework = points still needed from the exam.
const TargetTab = () => {
  const [cwEarnedInput, setCwEarnedInput] = useState('');
  const [examWeight, setExamWeight] = useState(40);
  const [paperTotalInput, setPaperTotalInput] = useState('');

  // Raw marks the exam paper is out of; percentages when left at 100.
  const paperTotalRaw = parseFloat(paperTotalInput);
  const paperTotal = !Number.isNaN(paperTotalRaw) && paperTotalRaw > 0 ? paperTotalRaw : 100;

  const cwWeight = 100 - examWeight;
  const cwEarnedRaw = parseFloat(cwEarnedInput);
  const hasInput = !Number.isNaN(cwEarnedRaw);
  const cwEarned = hasInput ? Math.max(0, cwEarnedRaw) : 0;
  const overWeight = hasInput && cwEarned > cwWeight;
  const cwCounted = Math.min(cwEarned, cwWeight);

  // Per grade band: points still needed = band minimum − coursework earned;
  // raw mark on the paper = (points needed ÷ exam weight) × paper total,
  // rounded UP so the displayed mark actually clears the threshold.
  const tiers = USP_GRADE_SCALE.filter(tier => tier.grade !== 'E').map(tier => {
    const neededPoints = tier.minPercent - cwCounted;
    if (neededPoints <= 0) {
      return { ...tier, status: 'secured' };
    }
    const rawNeeded = Math.ceil((neededPoints / examWeight) * paperTotal);
    if (rawNeeded > paperTotal) {
      return { ...tier, status: 'unreachable' };
    }
    return {
      ...tier,
      status: 'possible',
      rawNeeded,
      examPercent: (neededPoints / examWeight) * 100,
    };
  });

  return (
    <section className="rounded-[28px] bg-gradient-to-b from-graphite-900/95 to-graphite-700/90 backdrop-blur-xl border border-white/10 text-white px-5 pt-6 pb-7 sm:px-6 shadow-xl shadow-slate-900/25">
      <h2 className="text-[22px] font-semibold tracking-tight">Exam target</h2>
      <p className="mt-1 text-sm text-white/65">
        What you need on the final, grade by grade.
      </p>

      <div className="mt-6">
        <label htmlFor="cw-earned" className="block text-[13px] font-medium text-gold-200">
          Coursework earned so far
        </label>
        <div className="mt-2 flex items-center gap-3">
          <input
            id="cw-earned"
            type="number"
            inputMode="decimal"
            min="0"
            max="100"
            step="0.5"
            value={cwEarnedInput}
            onChange={(e) => setCwEarnedInput(e.target.value)}
            placeholder={`e.g. ${Math.round(cwWeight * 0.75)}`}
            className="w-full rounded-full bg-white text-slate-900 placeholder-slate-900/35 px-5 py-3.5 text-[17px] font-medium outline-none focus:ring-2 focus:ring-gold-300"
          />
          <span className="text-white/70 text-[15px] whitespace-nowrap font-medium">
            of {cwWeight}%
          </span>
        </div>
        {overWeight && (
          <p className="mt-2 text-xs text-gold-200">
            That&apos;s more than the {cwWeight}% coursework carries, counting it as {cwWeight}%.
          </p>
        )}
      </div>

      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <label htmlFor="exam-weight" className="text-[13px] font-medium text-gold-200">
            Final exam weight
          </label>
          <span className="text-[17px] font-semibold tabular-nums">{examWeight}%</span>
        </div>
        <input
          id="exam-weight"
          type="range"
          min="10"
          max="90"
          step="5"
          value={examWeight}
          onChange={(e) => setExamWeight(parseInt(e.target.value, 10))}
          className="slider mt-3 w-full"
        />
        <p className="mt-2 text-xs text-white/50">
          Coursework {cwWeight}% + final {examWeight}% = 100%
        </p>
      </div>

      <div className="mt-6">
        <label htmlFor="paper-total" className="block text-[13px] font-medium text-gold-200">
          Exam paper marked out of
        </label>
        <input
          id="paper-total"
          type="number"
          inputMode="numeric"
          min="1"
          step="1"
          value={paperTotalInput}
          onChange={(e) => setPaperTotalInput(e.target.value)}
          placeholder="e.g. 80, leave empty for 100"
          className="mt-2 w-full rounded-full bg-white text-slate-900 placeholder-slate-900/35 px-5 py-3.5 text-[17px] font-medium outline-none focus:ring-2 focus:ring-gold-300"
        />
        <p className="mt-2 text-xs text-white/50">
          The total raw marks printed on the final exam paper.
        </p>
      </div>

      <div className="mt-7 pt-5 border-t border-white/15">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gold-200">
          Needed on the final
        </p>

        {hasInput ? (
          <ul className="mt-2 divide-y divide-white/10">
            {tiers.map(tier => (
              <li key={tier.grade} className="flex items-center justify-between py-3">
                <div className="flex items-baseline gap-2.5">
                  <span className="text-xl font-semibold w-9">{tier.grade}</span>
                  <span className="text-xs text-white/50">{tier.gpa.toFixed(1)} GPA</span>
                </div>
                {tier.status === 'secured' ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gold-300">
                    <Check className="w-4 h-4" /> Already secured
                  </span>
                ) : tier.status === 'possible' ? (
                  <span className="text-right">
                    <span className="text-xl font-semibold tabular-nums">
                      {tier.rawNeeded}
                      <span className="text-sm font-normal text-white/50"> / {paperTotal}</span>
                    </span>
                    <span className="block text-[11px] text-white/50">
                      {Math.ceil(tier.examPercent)}% of the exam
                    </span>
                  </span>
                ) : (
                  <span className="text-sm text-white/40">Not achievable</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-white/55 leading-relaxed">
            Enter your coursework points above, say you&apos;ve earned 50 of the 60%
            coursework carries, and every grade tier appears here.
          </p>
        )}

        {hasInput && (
          <p className="mt-4 text-xs text-white/45 leading-relaxed">
            Raw marks are what you need to score on the paper itself, rounded up.
            Tiers follow the USP scale (A+ from 85%, A from 80%, …).
          </p>
        )}
      </div>
    </section>
  );
};

export default TargetTab;
